import React from 'react';
import { AndFilterInput, EntityType, PlatformPrivileges } from '../../types.generated';
import { colorUtilService } from '../shared/charts';
import { DonutDataItem } from '../shared/charts/chartConfigUtilService';
import { USER_SEARCH_QUERY_FILTER } from '../search/filters/searchFilterConstantsService';
import { DOMAINS_FILTER_NAME, OWNERS_FILTER_NAME } from '../search/utils/constants';

export const DASHBOARD_TILE_TYPES = [
    EntityType.Dataset,
    EntityType.DataProduct,
    EntityType.GlossaryTerm,
    EntityType.Domain,
    EntityType.Tag,
    EntityType.Chart,
];

const { donutColorMap } = colorUtilService;

// Colors for the top named items -- GRAY_S20 is intentionally excluded
// as it is reserved exclusively for the "Other" slice.
// Order alternates between color families so no two adjacent slices look similar.
export const WIDGET_CHART_COLORS = [
    donutColorMap.GRAY_S80,
    donutColorMap.BLUE_S50,
    donutColorMap.BLUE_S100,
    donutColorMap.GREEN_S100,
];

// Light grey reserved for the "Other" slice -- must not appear in WIDGET_CHART_COLORS
export const WIDGET_OTHER_COLOR = '#C5D0D3'; // GRAY_S20

export const WIDGET_TOP_ITEMS_LIMIT = 4;

/**
 * Appends an "Other" slice to chart data if there are remaining items beyond the top limit.
 * Mutates the passed chartData array in place and returns it.
 */
export function appendOtherSlice(
    chartData: DonutDataItem[],
    remainingTotal: number,
    otherLabel: string,
): DonutDataItem[] {
    if (remainingTotal > 0) {
        chartData.push({
            name: otherLabel,
            y: remainingTotal,
            color: WIDGET_OTHER_COLOR,
        });
    }
    return chartData;
}

export const WIDGET_LOADING_STYLE: React.CSSProperties = {
    height: 300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

export const COMMON_ASSET_ENTITY_TYPES = [
    EntityType.Dataset,
    EntityType.Chart,
    EntityType.Dashboard,
    EntityType.DataJob,
    EntityType.DataFlow,
];

export const PLATFORMS_MODULE_ID = 'Platforms';
export const MOST_POPULAR_MODULE_ID = 'HighUsageEntities';
export const TOP_TERMS_MODULE_ID = 'TopTerms';
export const TOP_TAGS_MODULE_ID = 'TopTags';
export const RECENTLY_VIEWED_MODULE_ID = 'RecentlyViewedEntities';
export const RECENTLY_EDITED_MODULE_ID = 'RecentlyEditedEntities';

export const getPrivilegeRestrictions = (platformPrivileges: PlatformPrivileges | null | undefined) => ({
    isDomainsRestricted: !platformPrivileges?.manageDomains && !platformPrivileges?.viewDomain,
    isPlatformsRestricted: !platformPrivileges?.manageDataPlatform,
    isTopTagsRestricted: !platformPrivileges?.manageTags && !platformPrivileges?.viewTag,
    isTopGlossaryTermsRestricted: !platformPrivileges?.viewGlossaryTerm && !platformPrivileges?.manageGlossaryTerms,
    isUsersRestricted: !platformPrivileges?.manageUsers,
    isCheckpointScoreRestricted: !platformPrivileges?.readDataQualityCheckpoint,
});

// Keep raw translation keys here (not pre-translated strings). This config is built
// once at module load time, when i18next may still be on the default language and the
// real language file has not been loaded yet. Translating at render time via t(labelKey)
// ensures the labels react to the active language.
export const DASHBOARD_TILE_CONFIG = [
    {
        entityType: EntityType.Dataset,
        labelKey: 'Datasets',
        colorClass: 'f-color-dark-black-s70',
    },
    {
        entityType: EntityType.DataProduct,
        labelKey: 'Data Products',
        colorClass: 'f-color-blue-s100',
    },
    {
        entityType: EntityType.GlossaryTerm,
        labelKey: 'Glossary Terms',
        colorClass: 'f-color-securiti-green',
    },
    {
        entityType: EntityType.Domain,
        labelKey: 'Domains',
        colorClass: 'f-color-dark-black-s50',
    },
    {
        entityType: EntityType.Tag,
        labelKey: 'Tags',
        colorClass: 'f-color-dark-black-s50',
    },
    {
        entityType: EntityType.Chart,
        labelKey: 'Charts',
        colorClass: 'f-color-blue-s100',
    },
];

// Each entry drives one TopSelector in the dashboard filter bar.
// To add a new filter, append an entry here — no JSX changes needed in DashboardV2.
export interface FilterSelectorConfig {
    // Translation key for the selector label.
    titleKey: string;
    // Entity type used to drive recommendations and search inside TopSelector.
    entityType: EntityType;
    // The dashboard filter field this selector writes to.
    field: string;
    // Optional orFilters applied to both recommendations and search (e.g. active-only users).
    filter?: AndFilterInput[];
}

export const FILTER_SELECTOR_CONFIG: FilterSelectorConfig[] = [
    {
        titleKey: 'Domain',
        entityType: EntityType.Domain,
        field: DOMAINS_FILTER_NAME,
    },
    {
        titleKey: 'Owner',
        entityType: EntityType.CorpUser,
        field: OWNERS_FILTER_NAME,
        filter: USER_SEARCH_QUERY_FILTER,
    },
];

// Minimal structural shapes shared by the aggregateAcrossEntities query results across widgets.
interface WidgetAggregation {
    value: string;
    count: number;
    entity?: { __typename?: string } | null;
}

// Returns a fresh, mutable copy of the aggregations for the given facet field
// (empty when the facet is missing), so callers can safely sort the result in place.
export function getFacetAggregations<A extends WidgetAggregation>(
    facets: ReadonlyArray<{ field: string; aggregations?: readonly A[] | null }> | null | undefined,
    field: string,
): A[] {
    return [...(facets?.find((facet) => facet.field === field)?.aggregations ?? [])];
}

// The entity name carried on an aggregation, when present (centralizes the cast over the
// broad entity union, whose members expose `properties.name` with differing shapes).
export function getAggEntityName(agg: WidgetAggregation): string | undefined {
    const entity = agg.entity as { properties?: { name?: string | null } | null } | null | undefined;
    return entity?.properties?.name ?? undefined;
}

// Resolves an aggregation's human-readable name, falling back to the trailing segment
// of the urn-like value (e.g. 'urn:li:domain:Marketing' -> 'Marketing').
export function getAggDisplayName(agg: WidgetAggregation, fallback = 'Unknown'): string {
    return getAggEntityName(agg) || agg.value.split(':').pop() || fallback;
}
